import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { goToFollowing, goToSearch, pathnames } from 'utils/routerHelpers';
import { truncateText } from 'utils/textHelpers';

import React, { useMemo } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { BookmarkSimple, CaretDown, MagnifyingGlass } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import { NotificationPane } from 'components/NotificationPane';
import UserAvatar from 'components/UserAvatar';
import CommunityStatusBadge from 'components/new/CommunityStatusBadge/CommunityStatusBadge';
import { CommunitySwitcherPopover } from 'components/new/layout/CommunitySwitcherPopover/CommunitySwitcherPopover';

import { BaseGlobalHeader } from './GlobalHeader';

const LeftContent = () => {
  const { community } = useCommunityContext();

  return (
    <>
      <CommunitySwitcherPopover>
        <Button
          data-user-pilot-id="community-switcher"
          emphasis="low"
          icon={CaretDown}
          size="xs"
          hiddenLabel
        >
          {community.name}
        </Button>
      </CommunitySwitcherPopover>
      <CommunityStatusBadge />
    </>
  );
};

const CommunityGlobalHeader = ({ title }: { title?: string }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { trackEvent, community, currentUser, inLearnerHub } = useCommunityContext();

  const truncatedCommunityName = useMemo(() => truncateText(community.name, 75), [community.name]);

  return (
    <BaseGlobalHeader
      leftContent={<LeftContent />}
      rightContent={
        <>
          <Button
            data-qa="main-search_button"
            emphasis="low"
            href={`/communities/${community.id}/search/`}
            icon={MagnifyingGlass}
            iconStyle={router.pathname === pathnames.search ? 'filled' : 'default'}
            size="sm"
            hiddenLabel
            onClick={(e) => {
              e.preventDefault();
              trackEvent(TrackingEvent.GO_TO_SEARCH, {
                [TrackingProperty.COMPONENT]: 'GlobalHeader',
              });
              goToSearch(router, community.id);
            }}
          >
            {t('pages-search_title', 'Search')}
          </Button>
          <Button
            data-qa="main-following__button"
            emphasis="low"
            href={`/communities/${community.id}/following/`}
            icon={BookmarkSimple}
            iconStyle={router.pathname === pathnames.following ? 'filled' : 'default'}
            size="sm"
            hiddenLabel
            onClick={(e) => {
              e.preventDefault();
              trackEvent(TrackingEvent.GO_TO_FOLLOWING, {
                [TrackingProperty.COMPONENT]: 'GlobalHeader',
              });
              goToFollowing(router, community.id);
            }}
          >
            {t('pages-following_title', 'Following')}
          </Button>
          {!inLearnerHub && (
            <>
              <NotificationPane />
              <UserAvatar
                size="xs"
                {...currentUser}
                userId={currentUser.id}
                username={currentUser.fullName}
                shouldOpenSideMenu
              />
            </>
          )}
        </>
      }
      title={title || truncatedCommunityName}
    />
  );
};

export default CommunityGlobalHeader;
